import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { selectAllBranches } from '@user/store/branches'
import { selectAllCampaigns } from '@user/store/campaigns'
import { selectAllDivisions } from '@user/store/divisions/slice'
import { selectAllLocationTypes } from '@user/store/locationTypes'
import {
  selectAllSalesFunnels,
  selectAllSalesFunnelStages,
} from '@user/store/salesFunnel'
import { selectAllSalesTeams } from '@user/store/salesTeam'
import { selectAllSources } from '@user/store/sources'
import { selectAllTargetPests } from '@user/store/targetPests'
import { selectAllUsers } from '@user/store/users/slice'
import { options as bestContactTimes } from '@user/components/Formik/BestContactTimeSelect/hooks/useData'
import { options as bestContactMethods } from '@user/components/Formik/BestContactMethodSelect/hooks/useData'
import { selectAllServiceProductsForTenant } from '@user/store/serviceProducts'
import { selectAllServiceFrequencies } from '@user/store/serviceFrequency'

export function useRowData() {
  const currentUser = useSelector(selectCurrentUser)
  const allSalesTeams = useSelector(selectAllSalesTeams)
  const allBranches = useSelector(selectAllBranches)
  const allSalesFunnels = useSelector(selectAllSalesFunnels)
  const allTargetPests = useSelector(selectAllTargetPests)
  const allLocationTypes = useSelector(selectAllLocationTypes)
  const allSources = useSelector(selectAllSources)
  const allCampaigns = useSelector(selectAllCampaigns)
  const allDivisions = useSelector(selectAllDivisions)
  const allTenantUsers = useSelector(selectAllUsers)
  const allStages = useSelector(selectAllSalesFunnelStages)
  const allServices = useSelector(selectAllServiceProductsForTenant)
  const allServiceFrequencies = useSelector(selectAllServiceFrequencies)
  const allContactMethods = bestContactMethods
  const allContactTimes = bestContactTimes

  const allLeadStages = useMemo(
    () => allStages.filter((x) => x.sectionType === 'Lead'),
    [allStages],
  )

  const allOpportunityStages = useMemo(
    () => allStages.filter((x) => x.sectionType === 'Opportunity'),
    [allStages],
  )

  const allUsers = useMemo(
    () =>
      allTenantUsers
        .filter((x) => x.isActive)
        .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '')),
    [allTenantUsers],
  )

  return {
    currentUser,
    allBranches,
    allLeadStages,
    allOpportunityStages,
    allSalesTeams,
    allSalesFunnels,
    allUsers,
    allServices,
    allServiceFrequencies,
    allSources,
    allCampaigns,
    allTargetPests,
    allLocationTypes,
    allDivisions,
    allContactTimes,
    allContactMethods,
    allStages,
  }
}

export type UseRowData = ReturnType<typeof useRowData>
